import type { ComponentProps } from 'react';

import type {
	AgentSessionState,
	ApiTaskSSEUpdate,
	ApiTaskStatus,
	ConversationChannelMessageStatus,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { LoadingStatus } from '@atlassian/rovo-platform-ui-components/primitives/LoadingStatusIndicator';
import type { SessionStatusBadge } from '@atlassian/rovo-platform-ui-components/session-details/SessionStatusBadge';

type SessionStatusBadgeStatus = ComponentProps<typeof SessionStatusBadge>['status'];

/** Minimal conversation shape for row status (task SSE + legacy channel status). */
export type ConversationListRowStatusSource = {
	currentTask?: ApiTaskSSEUpdate | null;
	lastAgentMessageStatus?: ConversationChannelMessageStatus;
};

export const getAgentSessionStateBadgeStatus = (
	agentSessionState?: AgentSessionState,
): SessionStatusBadgeStatus | undefined => {
	switch (agentSessionState) {
		case 'INPUT_REQUIRED':
		case 'AUTH_REQUIRED':
			return 'needs-input';
		default:
			return undefined;
	}
};

/** Loading status for the row chip / footer label (mirrors side-nav + full-screen list). */
export function getConversationListRowLoadingStatus(
	conversation: ConversationListRowStatusSource,
	isUnread: boolean,
): LoadingStatus | undefined {
	const { currentTask, lastAgentMessageStatus } = conversation;

	if (currentTask) {
		return getConversationListIndicatorFromTaskStatus(currentTask.status, isUnread);
	}

	if (lastAgentMessageStatus === 'IN_PROGRESS') return 'loading';
	if (lastAgentMessageStatus === 'FAILED') return 'error';
	// 'CANCELLED' (user-initiated Stop) intentionally returns undefined.
	return undefined;
}

/** Task states where the agent is blocked on user input (plan, disambiguation, or action confirmation). */
export function isTaskStatusAwaitingUserResponse(taskStatus: ApiTaskStatus | undefined): boolean {
	return (
		taskStatus === 'AWAITING_DISAMBIGUATION' ||
		taskStatus === 'AWAITING_PLAN_CONFIRMATION' ||
		taskStatus === 'AWAITING_ACTION_CONFIRMATION'
	);
}

/** Task states that drive list-row loading UI (spinner overlay, shimmer traces). */
export function isTaskStatusLoadingForConversationRow(
	taskStatus: ApiTaskStatus | undefined,
): boolean {
	return taskStatus === 'DRAFT' || taskStatus === 'QUEUED' || taskStatus === 'RUNNING';
}

/**
 * Maps live task lifecycle (`ApiTaskStatus`) to the conversation list row status chip.
 * Success applies when unread. Info for awaiting-user states always shows so users still
 * see action required after opening the conversation.
 */
export function getConversationListIndicatorFromTaskStatus(
	taskStatus: ApiTaskStatus,
	isUnread: boolean,
): LoadingStatus | undefined {
	if (isTaskStatusLoadingForConversationRow(taskStatus)) {
		return 'loading';
	}
	switch (taskStatus) {
		case 'AWAITING_DISAMBIGUATION':
		case 'AWAITING_PLAN_CONFIRMATION':
		case 'AWAITING_ACTION_CONFIRMATION':
			return 'info';
		case 'COMPLETED':
			return isUnread ? 'success' : undefined;
		case 'FAILED':
			return 'error';
		default:
			return undefined;
	}
}
